import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import React, { FC } from 'react';

type TStepSourceType = 'start' | 'join';
type TStepType =
  | TStepSourceType
  | 'summarise'
  | 'sort'
  | 'filter'
  | 'column-show'
  | 'column-rename'
  | 'column-reorder'
  | 'column-add';

type StepTypeProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  $isClickable?: boolean;
};

export const StepType: FC<StepTypeProps> = styled.button`
  cursor: ${(p) => (p.onClick ? 'pointer' : 'default')};
  white-space: nowrap;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-s);
  border: none;
  padding-left: var(--spacing-s);
  padding-right: var(--spacing-s);
  transition: all var(--transition-normal);
  background: var(--color-interface-layout-lighter);
  border-radius: calc((var(--spacing-xxs) * 2));
  color: #474752;
  font-weight: 500;
  font-size: 12px;
  line-height: var(--spacing-l);
  height: var(--spacing-xl);
  margin: calc((var(--spacing-xxs) * 2));
`;

const yellowCSSVars = css`
  --l-background: var(--color-interface-primary-lightest);
`;

const blueCSSVars = css`
  --l-background: var(--color-brand-blue1-lighter);
`;

const greenCSSVars = css`
  --l-background: var(--color-brand-green1-lighter);
`;

const orangeCSSVars = css`
  --l-background: var(--color-brand-orange1-lighter);
`;

const redCSSVars = css`
  --l-background: var(--color-brand-red1-lighter);
`;

const typeCssVars: Record<TStepType, FlattenSimpleInterpolation> = {
  start: yellowCSSVars,
  join: yellowCSSVars,
  sort: blueCSSVars,
  filter: blueCSSVars,
  'column-show': orangeCSSVars,
  'column-rename': greenCSSVars,
  'column-reorder': orangeCSSVars,
  'column-add': greenCSSVars,
  summarise: redCSSVars,
};

type StepValueProps = StepTypeProps &
  (
    | {
        $type: TStepType;
      }
    | { $type: TStepSourceType; isSavedQuery: boolean }
  );

export const StepValueAction = styled.div`
  padding-right: calc((var(--spacing-xxs) * 2));
  padding-left: calc((var(--spacing-xxs) * 2));
  border-radius: calc((var(--spacing-xxs) * 2));
  height: 100%;
  display: flex;
  align-items: center;
  transition: all var(--transition-normal);

  :hover {
    background: var(--overlay-light-50);
  }

  :active {
    background: var(--overlay-light-25);
  }
`;

export const StepValue: FC<StepValueProps> = styled(StepType)`
  ${(p) => typeCssVars[p.$type as TStepType]}

  background: var(--l-background);

  :hover {
    height: 40px;
    padding-left: var(--spacing-m);
    padding-right: var(--spacing-m);
    margin: 0;
  }

  :has(div) {
    padding: var(--spacing-xxs) var(--spacing-xxs) var(--spacing-xxs) var(--spacing-s);

    :hover {
      padding: var(--spacing-xxs) var(--spacing-xxs) var(--spacing-xxs) var(--spacing-m);

      > div:first-of-type {
        margin-left: calc((var(--spacing-xxs) * 2));
      }
    }
  }

  ${(p) =>
    p.isSavedQuery &&
    css`
      background: var(--color-brand-purple1-lighter);
    `}
}
`;
